<script setup lang="ts">
import { IconFingerprint, IconMail } from '@tabler/icons-vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { alerts, createAlert } from '../../alerts.ts';
import BrowserLink from '../../components/BrowserLink.vue';
import PasswordInput from '../../components/PasswordInput.vue';
import { AuthenticationRequestError, authRequest, type AuthenticationResult } from '../api.ts';
import { authenticationErrorMessage, useAuthenticationAction } from '../authenticationAction.ts';
import {
    authenticateWithPasskey,
    authenticateWithPasskeyAutofill,
    isPasskeyCancellation,
    passkeySupport,
} from '../passkeys.ts';
import type { AuthenticationDestination, AuthenticationStart } from '../types.ts';
import AuthEmailSent from './AuthEmailSent.vue';

type EntryStart = Extract<AuthenticationStart, { screen: 'entry' }>;

const props = defineProps<{
    start: EntryStart;
    intended?: string;
    autofocus?: boolean;
    completing: boolean;
    resolve: (result: AuthenticationResult) => Promise<void>;
}>();

const emit = defineEmits<{
    navigate: [destination: AuthenticationDestination];
}>();

const emailSent = ref(false);
const email = ref(props.start.mode === 'reauthenticate' ? props.start.email : '');
const password = ref('');
const credentialError = ref('');
const passkeySupported = ref(false);
const emailInput = ref<HTMLInputElement>();
const pendingAction = ref<'submit' | 'passkey' | 'email'>();
const passkeyAutofillController = new AbortController();
const { busy, run } = useAuthenticationAction();
const loading = computed(() => busy.value || props.completing);
const purpose = computed(() =>
    props.start.mode === 'reauthenticate' ? props.start.purpose : 'login',
);
const confirming = computed(
    () => props.start.mode === 'reauthenticate' && props.start.purpose === 'confirm',
);
const allowPasskey = computed(
    () =>
        props.start.mode !== 'register' &&
        passkeySupported.value &&
        (props.start.mode === 'login' || props.start.methods.passkey),
);
const allowPassword = computed(
    () =>
        props.start.mode === 'register' ||
        props.start.mode === 'login' ||
        props.start.methods.password,
);
const allowLogin = computed(
    () => props.start.mode === 'register' && !props.start.registration?.referrer_id,
);

watch([email, password], () => (credentialError.value = ''));

onMounted(async () => {
    if (props.autofocus) {
        emailInput.value?.focus();
    }

    const support = await passkeySupport().catch(() => undefined);
    passkeySupported.value = support?.available ?? false;

    if (support?.platform === 'web' && support.conditionalMediation) {
        startPasskeyAutofill();
    }
});

onBeforeUnmount(stopPasskeyAutofill);

function canAutofillPasskey() {
    return (
        !emailSent.value &&
        !passkeyAutofillController.signal.aborted &&
        (props.start.mode === 'login' ||
            (props.start.mode === 'reauthenticate' &&
                props.start.purpose === 'confirm' &&
                props.start.methods.passkey))
    );
}

function stopPasskeyAutofill() {
    passkeyAutofillController.abort();
}

function startPasskeyAutofill() {
    if (!canAutofillPasskey()) {
        return;
    }

    let credentialSelected = false;

    void authenticateWithPasskeyAutofill(
        purpose.value,
        props.intended,
        passkeyAutofillController.signal,
        () => {
            credentialSelected = true;
            busy.value = true;
        },
    )
        .then(props.resolve)
        .catch((reason) => {
            if (credentialSelected && !isPasskeyCancellation(reason)) {
                alerts.show(
                    createAlert({ type: 'danger', message: authenticationErrorMessage(reason) }),
                );
            }
        })
        .finally(() => {
            if (!passkeyAutofillController.signal.aborted) {
                busy.value = false;
            }
        });
}

function sendEmailLink() {
    stopPasskeyAutofill();

    void run(async () => {
        await authRequest('links', 'POST', {
            email: email.value,
            mode: props.start.mode === 'register' ? 'register' : 'login',
            purpose: purpose.value,
            intended: props.intended,
            registration: props.start.mode === 'register' ? props.start.registration : undefined,
        });
        emailSent.value = true;
    });
}

function submit() {
    pendingAction.value = 'submit';

    if (props.start.mode === 'register') {
        sendEmailLink();
    } else {
        authenticateWithPassword();
    }
}

function emailLink() {
    if (emailInput.value?.reportValidity() !== false) {
        pendingAction.value = 'email';
        sendEmailLink();
    }
}

function authenticateWithPassword() {
    stopPasskeyAutofill();
    credentialError.value = '';

    void run(
        async () => {
            const result =
                purpose.value === 'confirm'
                    ? await authRequest<AuthenticationResult>('confirm/password', 'POST', {
                          password: password.value,
                      })
                    : await authRequest<AuthenticationResult>('password', 'POST', {
                          email: email.value,
                          password: password.value,
                          intended: props.intended,
                      });

            password.value = '';
            await props.resolve(result);
        },
        (reason) => {
            const field = purpose.value === 'confirm' ? 'password' : 'email';

            if (
                reason instanceof AuthenticationRequestError &&
                reason.response.status === 422 &&
                Array.isArray(reason.data?.errors?.[field])
            ) {
                credentialError.value =
                    purpose.value === 'confirm'
                        ? 'That password is incorrect.'
                        : 'We couldn’t sign you in with that email and password. Check both fields and try again, or use the Email Me a Sign-In Link button.';

                return true;
            }
        },
    );
}

function usePasskey() {
    stopPasskeyAutofill();
    pendingAction.value = 'passkey';
    void run(async () => {
        await props.resolve(await authenticateWithPasskey(purpose.value, props.intended));
    }, isPasskeyCancellation);
}

function navigate(destination: AuthenticationDestination) {
    stopPasskeyAutofill();
    emit('navigate', destination);
}
</script>

<template>
    <form v-if="!emailSent" class="stack gap-lg" @submit.prevent="submit">
        <div class="stack align-center gap-md text-center">
            <h2 v-if="start.mode !== 'reauthenticate'">
                {{ start.mode === 'register' ? 'Create Your Account' : 'Sign In' }}
            </h2>
            <p v-if="start.mode === 'reauthenticate'" class="text-sm">
                Continue as <strong>{{ email }}</strong>
            </p>
            <p v-else-if="start.mode === 'login'" class="text-sm color-muted">
                New to eMedici?
                <button
                    type="button"
                    class="link weight-medium"
                    :disabled="loading"
                    @click="navigate('register')"
                >
                    Create an Account
                </button>
            </p>
            <p v-else class="color-muted text-sm wrap-balance">
                We’ll email you a secure link to verify your address and continue.
            </p>
        </div>

        <div v-if="!confirming" class="stack gap-xs">
            <label for="auth-email" class="field-label">Email</label>
            <input
                id="auth-email"
                ref="emailInput"
                v-model="email"
                class="input text-lg"
                :class="{ 'input--error': credentialError }"
                type="email"
                autocomplete="email webauthn"
                maxlength="255"
                :aria-invalid="Boolean(credentialError)"
                :disabled="loading"
                :readonly="start.mode === 'reauthenticate'"
                required
            />
        </div>

        <div v-if="start.mode !== 'register' && allowPassword" class="stack gap-xs">
            <div class="row justify-between field-label">
                <label for="auth-password">Password</label>
                <button
                    v-if="start.mode === 'login'"
                    type="button"
                    class="link"
                    :disabled="loading"
                    @click="navigate('password-recovery')"
                >
                    Forgot Password?
                </button>
            </div>
            <PasswordInput
                id="auth-password"
                name="password"
                v-model="password"
                class="text-lg"
                :class="{ 'input--error': credentialError }"
                :autocomplete="
                    confirming && allowPasskey ? 'current-password webauthn' : 'current-password'
                "
                :aria-describedby="credentialError ? 'auth-credential-error' : undefined"
                :aria-invalid="Boolean(credentialError)"
                :disabled="loading"
                required
                :autofocus="confirming"
            />
            <p
                v-if="credentialError"
                id="auth-credential-error"
                data-testid="auth-credential-error"
                class="color-danger text-xs"
                role="alert"
            >
                {{ credentialError }}
            </p>
        </div>

        <div class="stack gap-sm">
            <button
                v-if="allowPassword"
                data-testid="auth-submit"
                class="btn btn--lg bg-primary full-width"
                :class="{ 'is-loading': loading && pendingAction === 'submit' }"
                :disabled="loading"
            >
                {{
                    start.mode === 'register'
                        ? 'Continue'
                        : confirming
                          ? 'Confirm With Password'
                          : 'Sign In'
                }}
            </button>

            <button
                v-if="start.mode !== 'register' && allowPasskey"
                type="button"
                class="btn btn--lg gap-sm full-width"
                :class="{ 'is-loading': loading && pendingAction === 'passkey' }"
                :disabled="loading"
                @click="usePasskey"
            >
                <IconFingerprint class="icon" />
                {{ confirming ? 'Confirm With a Passkey' : 'Sign In With a Passkey' }}
            </button>
            <button
                v-if="start.mode !== 'register'"
                type="button"
                class="btn btn--lg gap-sm full-width"
                :class="{ 'is-loading': loading && pendingAction === 'email' }"
                :disabled="loading"
                @click="emailLink"
            >
                <IconMail class="icon" />
                Email Me a Sign-In Link
            </button>
        </div>

        <p v-if="start.mode === 'register'" class="text-xs text-center">
            By continuing, you agree to our<br />
            <BrowserLink href="https://emedici.com/terms" class="nowrap weight-bold">
                Terms of Service
            </BrowserLink>
            and
            <BrowserLink href="https://emedici.com/privacy" class="nowrap weight-bold"
                >Privacy Policy</BrowserLink
            >.
        </p>

        <p v-if="allowLogin" class="text-xs text-center">
            Already have an account?
            <button
                type="button"
                class="link weight-bold"
                :disabled="loading"
                @click="navigate('login')"
            >
                Sign In
            </button>
        </p>
    </form>
    <AuthEmailSent
        v-else
        :email="email"
        :confirming="purpose === 'confirm'"
        @back="emailSent = false"
    />
</template>
